import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiCopy,
  FiGlobe,
  FiPhoneCall,
  FiRefreshCw,
  FiRotateCw,
  FiSave,
  FiShield,
  FiTrash2,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import axios from "../../api/axiosInstance";

const crmProviderAdminCss = `
  .crmProviderPage {
    width: 100%;
    padding: 0 18px 18px;
  }

  .crmProviderCard {
    position: relative;
    border-radius: 18px;
    border: 1px solid transparent;
    background:
      linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box,
      linear-gradient(135deg, rgba(48, 216, 255, 0.45), rgba(168, 85, 247, 0.52), rgba(255, 77, 79, 0.35)) border-box;
    box-shadow: 0 28px 90px rgba(0, 0, 0, 0.58);
    overflow: hidden;
  }

  .crmProviderCard::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(900px 300px at 10% 0%, rgba(48, 216, 255, 0.14), transparent 60%),
      radial-gradient(700px 260px at 90% 10%, rgba(168, 85, 247, 0.16), transparent 62%),
      radial-gradient(800px 260px at 40% 110%, rgba(255, 77, 79, 0.10), transparent 60%);
    pointer-events: none;
  }

  .crmProviderCard > * {
    position: relative;
    z-index: 1;
  }

  .crmProviderHeader {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    padding: 18px 18px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .crmProviderKicker {
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: rgba(48, 216, 255, 0.76);
    margin-bottom: 6px;
  }

  .crmProviderTitle {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 850;
    color: transparent;
    background: linear-gradient(90deg, #e6e6f7 0%, rgba(48, 216, 255, 0.95) 40%, rgba(168, 85, 247, 0.95) 85%);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .crmProviderSubtitle {
    margin-top: 4px;
    font-size: 0.86rem;
    color: rgba(230, 230, 247, 0.72);
  }

  .crmProviderBody {
    display: grid;
    grid-template-columns: minmax(280px, 0.8fr) minmax(0, 1.2fr);
    gap: 16px;
    padding: 18px;
  }

  .crmProviderSummary {
    padding: 14px 18px 0;
  }

  .crmProviderSummaryHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
  }

  .crmProviderSummaryTitle {
    margin: 0;
    color: rgba(230, 230, 247, 0.96);
    font-size: 0.96rem;
    font-weight: 900;
  }

  .crmProviderSummaryCount {
    color: rgba(230, 230, 247, 0.62);
    font-size: 0.78rem;
    font-weight: 850;
  }

  .crmProviderBuList {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
  }

  .crmProviderBuCard {
    min-height: 108px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    background: rgba(255, 255, 255, 0.035);
    color: inherit;
    padding: 12px;
    text-align: left;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 10px;
  }

  .crmProviderBuCard--selected {
    border-color: rgba(48, 216, 255, 0.48);
    background: rgba(48, 216, 255, 0.08);
    box-shadow: inset 0 0 0 1px rgba(48, 216, 255, 0.12);
  }

  .crmProviderBuCard:hover {
    border-color: rgba(48, 216, 255, 0.34);
  }

  .crmProviderBuName {
    color: rgba(230, 230, 247, 0.96);
    font-size: 0.9rem;
    font-weight: 900;
    line-height: 1.25;
  }

  .crmProviderBuMeta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .crmProviderBuFoot {
    color: rgba(230, 230, 247, 0.58);
    font-size: 0.74rem;
    font-weight: 800;
  }

  .crmProviderBuEmpty {
    min-height: 92px;
    border-radius: 14px;
    border: 1px dashed rgba(255, 255, 255, 0.14);
    color: rgba(230, 230, 247, 0.58);
    background: rgba(255, 255, 255, 0.025);
    display: grid;
    place-items: center;
    font-weight: 850;
  }

  .crmProviderPanel {
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.04);
    padding: 14px;
  }

  .crmProviderPanelTitle {
    margin: 0 0 10px;
    color: rgba(230, 230, 247, 0.96);
    font-size: 1rem;
    font-weight: 850;
  }

  .crmProviderGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .crmProviderField {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .crmProviderField--wide {
    grid-column: 1 / -1;
  }

  .crmProviderField span,
  .crmProviderToggle span {
    color: rgba(230, 230, 247, 0.68);
    font-size: 0.78rem;
    font-weight: 850;
  }

  .crmProviderField input,
  .crmProviderField select {
    width: 100%;
    border-radius: 11px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    outline: 0;
    padding: 10px 11px;
    font: inherit;
    font-weight: 750;
  }

  .crmProviderField select {
    color-scheme: dark;
  }

  .crmProviderWebhookBox {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
  }

  .crmProviderWebhookBox input {
    font-size: 0.78rem;
  }

  .crmProviderCredentialsBox {
    grid-column: 1 / -1;
    border-radius: 14px;
    border: 1px solid rgba(48, 216, 255, 0.14);
    background: rgba(48, 216, 255, 0.045);
    padding: 12px;
  }

  .crmProviderCredentialsHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-bottom: 11px;
  }

  .crmProviderCredentialsTitle {
    margin: 0;
    color: rgba(230, 230, 247, 0.95);
    font-size: 0.96rem;
    font-weight: 900;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .crmProviderCredentialsHint {
    margin: 8px 0 0;
    color: rgba(230, 230, 247, 0.62);
    font-size: 0.78rem;
    line-height: 1.45;
  }

  .crmProviderWebRtcBox {
    grid-column: 1 / -1;
    border-radius: 14px;
    border: 1px solid rgba(57, 217, 138, 0.16);
    background: rgba(57, 217, 138, 0.045);
    padding: 12px;
  }

  .crmProviderSubHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-bottom: 11px;
  }

  .crmProviderSubTitle {
    margin: 0;
    color: rgba(230, 230, 247, 0.95);
    font-size: 0.96rem;
    font-weight: 900;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .crmProviderHint {
    margin: 9px 0 0;
    color: rgba(230, 230, 247, 0.62);
    font-size: 0.78rem;
    line-height: 1.45;
  }

  .crmProviderUserExtensionList {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
    max-height: 280px;
    overflow: auto;
    padding-right: 4px;
  }

  .crmProviderUserExtensionRow {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(110px, 160px);
    align-items: center;
    gap: 10px;
    min-height: 54px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.035);
    padding: 9px 10px;
  }

  .crmProviderUserName {
    color: rgba(230, 230, 247, 0.94);
    font-size: 0.85rem;
    font-weight: 900;
    line-height: 1.2;
  }

  .crmProviderUserEmail {
    color: rgba(230, 230, 247, 0.56);
    font-size: 0.76rem;
    font-weight: 750;
    overflow-wrap: anywhere;
  }

  .crmProviderToggle {
    min-height: 43px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.035);
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 11px;
  }

  .crmProviderToggle input {
    width: 16px;
    height: 16px;
  }

  .crmProviderActions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 14px;
  }

  .crmProviderButton {
    min-height: 40px;
    border-radius: 11px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.05);
    color: rgba(230, 230, 247, 0.9);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 13px;
    font: inherit;
    font-weight: 850;
    cursor: pointer;
  }

  .crmProviderButton--primary {
    color: #061010;
    background: #30d8ff;
    border-color: rgba(48, 216, 255, 0.55);
  }

  .crmProviderButton:disabled {
    opacity: 0.62;
    cursor: not-allowed;
  }

  .crmProviderBadge {
    min-height: 26px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 4px 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(230, 230, 247, 0.88);
    background: rgba(255, 255, 255, 0.04);
    font-size: 0.76rem;
    font-weight: 900;
    text-transform: capitalize;
  }

  .crmProviderBadge--active,
  .crmProviderBadge--configured,
  .crmProviderBadge--processed,
  .crmProviderBadge--valid {
    color: #c8fff0;
    border-color: rgba(57, 217, 138, 0.28);
    background: rgba(57, 217, 138, 0.12);
  }

  .crmProviderBadge--missing,
  .crmProviderBadge--draft,
  .crmProviderBadge--not_configured {
    color: #fff0c6;
    border-color: rgba(255, 214, 107, 0.3);
    background: rgba(255, 214, 107, 0.12);
  }

  .crmProviderBadge--disabled,
  .crmProviderBadge--error,
  .crmProviderBadge--rejected,
  .crmProviderBadge--invalid {
    color: #ffd7d7;
    border-color: rgba(255, 93, 93, 0.28);
    background: rgba(255, 93, 93, 0.1);
  }

  .crmProviderState {
    border-radius: 12px;
    padding: 11px 12px;
    margin-bottom: 12px;
    font-weight: 750;
  }

  .crmProviderState--info {
    color: #d6f6ff;
    border: 1px solid rgba(48, 216, 255, 0.22);
    background: rgba(48, 216, 255, 0.07);
  }

  .crmProviderState--error {
    color: #ffd7d7;
    border: 1px solid rgba(255, 93, 93, 0.3);
    background: rgba(135, 29, 29, 0.22);
  }

  .crmProviderTable {
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;
  }

  .crmProviderTable th,
  .crmProviderTable td {
    padding: 11px 12px;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    vertical-align: top;
  }

  .crmProviderTable th {
    color: rgba(230, 230, 247, 0.62);
    font-size: 0.74rem;
    text-transform: uppercase;
    font-weight: 900;
    background: rgba(255, 255, 255, 0.035);
  }

  .crmProviderTable td {
    color: rgba(230, 230, 247, 0.86);
    font-size: 0.84rem;
  }

  .crmProviderTableShell {
    overflow: auto;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.08);
  }

  .crmProviderEmpty {
    min-height: 130px;
    display: grid;
    place-items: center;
    color: rgba(230, 230, 247, 0.58);
    font-weight: 850;
  }

  @media (max-width: 980px) {
    .crmProviderHeader,
    .crmProviderBody {
      grid-template-columns: 1fr;
    }

    .crmProviderBody,
    .crmProviderHeader {
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }

    .crmProviderGrid {
      grid-template-columns: 1fr;
    }

    .crmProviderUserExtensionRow {
      grid-template-columns: 1fr;
    }
  }
`;

const defaultSettings = {
  create_missed_call_task: true,
  missed_call_task_priority: "high",
  require_signature: false,
  webrtc_widget_enabled: false,
  webrtc_widget_language: "es",
  webrtc_widget_shape: "square",
  webrtc_widget_position: "bottom_right",
  webrtc_widget_default_extension: "",
  webrtc_widget_user_extensions: {},
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (data?.error) return data.error;
  if (data?.message) return data.message;
  if (typeof data === "string" && data.trim()) {
    const trimmed = data.trim();
    return trimmed.length > 180 ? `${trimmed.slice(0, 180)}...` : trimmed;
  }
  if (err?.response?.status) {
    return `${fallback} (HTTP ${err.response.status})`;
  }
  if (err?.message) {
    return `${fallback}: ${err.message}`;
  }
  return fallback;
}

function formatZadarmaEvent(eventType) {
  const labels = {
    NOTIFY_START: "Incoming call started",
    NOTIFY_INTERNAL: "Incoming call routed",
    NOTIFY_ANSWER: "Call answered",
    NOTIFY_END: "Incoming call ended",
    NOTIFY_OUT_START: "Outgoing call started",
    NOTIFY_OUT_END: "Outgoing call ended",
    NOTIFY_RECORD: "Recording ready",
    NOTIFY_IVR: "IVR option selected",
  };
  const normalized = String(eventType || "").trim().toUpperCase();
  if (!normalized) return "-";
  return labels[normalized] || normalized.replace(/^NOTIFY_/, "").replace(/_/g, " ").toLowerCase();
}

function includesNumber(values, target) {
  const cleanTarget = Number(target);
  if (!Number.isFinite(cleanTarget)) return false;
  return Array.isArray(values) && values.map(Number).includes(cleanTarget);
}

function getUserRoleAssignments(user) {
  return Array.isArray(user?.role_assignments) ? user.role_assignments : [];
}

function userBelongsToBusinessUnit(user, businessUnitId) {
  if (!user || user.is_active === false) return false;
  if (user.has_access_to_all_business_units) return true;
  if (includesNumber(user.business_unit_ids, businessUnitId)) return true;
  return getUserRoleAssignments(user).some((assignment) =>
    Number(assignment.business_unit_id) === Number(businessUnitId)
  );
}

function formatUserDisplayName(user) {
  return user?.name || user?.email || `User ${user?.id || ""}`.trim();
}

export default function CrmProviderIntegrations() {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState("");
  const [status, setStatus] = useState("draft");
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [credentialsSaving, setCredentialsSaving] = useState(false);
  const [credentialsTesting, setCredentialsTesting] = useState(false);
  const [credentialsDeleting, setCredentialsDeleting] = useState(false);
  const [webRtcProvisioning, setWebRtcProvisioning] = useState(false);
  const [credentialsForm, setCredentialsForm] = useState({ api_key: "", api_secret: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const selectedIntegration = useMemo(
    () =>
      integrations.find(
        (integration) => String(integration.business_unit_id) === String(selectedBusinessUnitId)
      ) || null,
    [integrations, selectedBusinessUnitId]
  );

  const businessUnitNameById = useMemo(() => {
    const map = new Map();
    businessUnits.forEach((businessUnit) => {
      map.set(String(businessUnit.id), businessUnit.name);
    });
    return map;
  }, [businessUnits]);

  const configuredIntegrations = useMemo(
    () =>
      [...integrations].sort((a, b) => {
        const aName = a.business_unit?.name || businessUnitNameById.get(String(a.business_unit_id)) || "";
        const bName = b.business_unit?.name || businessUnitNameById.get(String(b.business_unit_id)) || "";
        return aName.localeCompare(bName);
      }),
    [businessUnitNameById, integrations]
  );

  const selectedBusinessUnitUsers = useMemo(
    () =>
      users
        .filter((user) => userBelongsToBusinessUnit(user, selectedBusinessUnitId))
        .sort((a, b) => formatUserDisplayName(a).localeCompare(formatUserDisplayName(b))),
    [selectedBusinessUnitId, users]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [businessUnitsResponse, integrationsResponse, usersResponse] = await Promise.all([
        axios.get("/api/business-units/all"),
        axios.get("/api/admin/crm-provider-integrations", { params: { provider: "zadarma" } }),
        axios.get("/api/admin/crm-provider-integrations/users"),
      ]);

      const nextBusinessUnits = Array.isArray(businessUnitsResponse.data)
        ? businessUnitsResponse.data
        : [];
      const nextIntegrations = Array.isArray(integrationsResponse.data?.rows)
        ? integrationsResponse.data.rows
        : [];
      const nextUsers = Array.isArray(usersResponse.data) ? usersResponse.data : [];

      setBusinessUnits(nextBusinessUnits);
      setIntegrations(nextIntegrations);
      setUsers(nextUsers);

      if (!selectedBusinessUnitId && nextBusinessUnits.length) {
        setSelectedBusinessUnitId(String(nextBusinessUnits[0].id));
      }
    } catch (err) {
      console.error("[CRM Admin] Provider integrations load error:", err);
      setError(getApiErrorMessage(err, "Could not load CRM provider integrations."));
    } finally {
      setLoading(false);
    }
  }, [selectedBusinessUnitId]);

  const loadLogs = useCallback(async (integrationId) => {
    if (!integrationId) {
      setLogs([]);
      return;
    }

    try {
      const response = await axios.get(`/api/admin/crm-provider-integrations/${integrationId}/webhook-logs`, {
        params: { limit: 25 },
      });
      setLogs(Array.isArray(response.data?.rows) ? response.data.rows : []);
    } catch (err) {
      console.error("[CRM Admin] Webhook logs load error:", err);
      setLogs([]);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedIntegration) {
      setStatus(selectedIntegration.status || "draft");
      setSettings({ ...defaultSettings, ...(selectedIntegration.settings || {}) });
      loadLogs(selectedIntegration.id);
    } else {
      setStatus("draft");
      setSettings(defaultSettings);
      setLogs([]);
    }
  }, [loadLogs, selectedIntegration]);

  useEffect(() => {
    setCredentialsForm({ api_key: "", api_secret: "" });
  }, [selectedIntegration?.id]);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const updateUserExtension = (userId, value) => {
    setSettings((prev) => {
      const nextExtensions = { ...(prev.webrtc_widget_user_extensions || {}) };
      const cleanValue = String(value || "").trim();

      if (cleanValue) {
        nextExtensions[String(userId)] = cleanValue;
      } else {
        delete nextExtensions[String(userId)];
      }

      return {
        ...prev,
        webrtc_widget_user_extensions: nextExtensions,
      };
    });
  };

  const updateCredentialsField = (key, value) => {
    setCredentialsForm((prev) => ({ ...prev, [key]: value }));
  };

  const selectIntegrationBusinessUnit = (businessUnitId) => {
    setSelectedBusinessUnitId(String(businessUnitId));
    setError("");
    setMessage("");
  };

  const saveIntegration = async () => {
    if (!selectedBusinessUnitId) return;
    setSaving(true);
    setError("");
    setMessage("");

    try {
      if (selectedIntegration?.id) {
        const response = await axios.patch(`/api/admin/crm-provider-integrations/${selectedIntegration.id}`, {
          name: "Zadarma",
          status,
          settings,
        });
        setIntegrations((prev) =>
          prev.map((item) => (item.id === response.data.id ? response.data : item))
        );
      } else {
        const response = await axios.post("/api/admin/crm-provider-integrations/zadarma", {
          business_unit_id: selectedBusinessUnitId,
          name: "Zadarma",
          status,
          settings,
        });
        setIntegrations((prev) => [...prev.filter((item) => item.id !== response.data.id), response.data]);
      }
      setMessage("Zadarma integration prepared.");
      await loadData();
    } catch (err) {
      console.error("[CRM Admin] Provider integration save error:", err);
      setError(getApiErrorMessage(err, "Could not save Zadarma integration."));
    } finally {
      setSaving(false);
    }
  };

  const rotateWebhookToken = async () => {
    if (!selectedIntegration?.id) return;
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/rotate-webhook-token`
      );
      setIntegrations((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
      setMessage("Webhook token rotated. Update Zadarma with the new URL.");
    } catch (err) {
      console.error("[CRM Admin] Rotate webhook token error:", err);
      setError(err?.response?.data?.error || "Could not rotate webhook token.");
    } finally {
      setSaving(false);
    }
  };

  const saveCredentials = async () => {
    if (!selectedIntegration?.id) {
      setError("Prepare the Zadarma integration before saving credentials.");
      return;
    }
    if (!credentialsForm.api_key.trim() || !credentialsForm.api_secret.trim()) {
      setError("API key and API secret are required.");
      return;
    }

    setCredentialsSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/credentials`,
        {
          api_key: credentialsForm.api_key,
          api_secret: credentialsForm.api_secret,
          settings,
        }
      );
      setIntegrations((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
      setSettings({ ...defaultSettings, ...(response.data.settings || {}) });
      setCredentialsForm({ api_key: "", api_secret: "" });
      setMessage("Zadarma credentials saved. Signature validation is enabled.");
    } catch (err) {
      console.error("[CRM Admin] Save provider credentials error:", err);
      setError(getApiErrorMessage(err, "Could not save Zadarma credentials."));
    } finally {
      setCredentialsSaving(false);
    }
  };

  const testConnection = async () => {
    if (!selectedIntegration?.id) return;

    setCredentialsTesting(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/test-connection`
      );
      const balance = response.data?.balance;
      const currency = response.data?.currency;
      setMessage(
        balance !== null && balance !== undefined
          ? `Zadarma connection OK. Balance: ${balance}${currency ? ` ${currency}` : ""}.`
          : "Zadarma connection OK."
      );
      await loadData();
    } catch (err) {
      console.error("[CRM Admin] Test provider connection error:", err);
      setError(getApiErrorMessage(err, "Could not test Zadarma connection."));
      await loadData();
    } finally {
      setCredentialsTesting(false);
    }
  };

  const deleteCredentials = async () => {
    if (!selectedIntegration?.id) return;
    const confirmed = window.confirm("Remove saved Zadarma credentials for this Business Unit?");
    if (!confirmed) return;

    setCredentialsDeleting(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.delete(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/credentials`
      );
      setIntegrations((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
      setSettings({ ...defaultSettings, ...(response.data.settings || {}) });
      setCredentialsForm({ api_key: "", api_secret: "" });
      setMessage("Zadarma credentials removed.");
    } catch (err) {
      console.error("[CRM Admin] Delete provider credentials error:", err);
      setError(getApiErrorMessage(err, "Could not remove Zadarma credentials."));
    } finally {
      setCredentialsDeleting(false);
    }
  };

  const provisionWebRtcDomain = async () => {
    if (!selectedIntegration?.id) {
      setError("Prepare the Zadarma integration before provisioning WebRTC.");
      return;
    }

    setWebRtcProvisioning(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/provision-webrtc`
      );

      if (response.data?.integration) {
        setIntegrations((prev) =>
          prev.map((item) => (item.id === response.data.integration.id ? response.data.integration : item))
        );
        setSettings({ ...defaultSettings, ...(response.data.integration.settings || {}) });
      }

      const domain = response.data?.domain || window.location.hostname;
      let action = "verified";
      if (response.data?.created) {
        action = "created";
      } else if (response.data?.domain_added) {
        action = "authorized";
      }
      setMessage(`Zadarma WebRTC ${action} for ${domain}. Reload CRM and try the widget again.`);
      await loadData();
    } catch (err) {
      console.error("[CRM Admin] Provision WebRTC error:", err);
      setError(getApiErrorMessage(err, "Could not provision Zadarma WebRTC."));
    } finally {
      setWebRtcProvisioning(false);
    }
  };

  const copyWebhookUrl = async () => {
    if (!selectedIntegration?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(selectedIntegration.webhook_url);
      setMessage("Webhook URL copied.");
    } catch (_err) {
      setMessage("Webhook URL is ready to copy manually.");
    }
  };

  return (
    <div className="crmProviderPage">
      <style>{crmProviderAdminCss}</style>
      <div className="crmProviderCard">
        <div className="crmProviderHeader">
          <div>
            <div className="crmProviderKicker">CRM Integrations</div>
            <h2 className="crmProviderTitle">Zadarma</h2>
            <div className="crmProviderSubtitle">
              Prepare PBX call webhooks, missed-call follow-ups and activity logging by Business Unit.
            </div>
          </div>
          <button type="button" className="crmProviderButton" onClick={loadData} disabled={loading}>
            <FiRefreshCw aria-hidden="true" />
            <span>{loading ? "Loading" : "Refresh"}</span>
          </button>
        </div>

        <section className="crmProviderSummary">
          <div className="crmProviderSummaryHeader">
            <h3 className="crmProviderSummaryTitle">Business Units with Zadarma</h3>
            <div className="crmProviderSummaryCount">
              {configuredIntegrations.length} configured
            </div>
          </div>

          {configuredIntegrations.length ? (
            <div className="crmProviderBuList">
              {configuredIntegrations.map((integration) => {
                const isSelected =
                  String(integration.business_unit_id) === String(selectedBusinessUnitId);
                const businessUnitName =
                  integration.business_unit?.name ||
                  businessUnitNameById.get(String(integration.business_unit_id)) ||
                  `Business Unit ${integration.business_unit_id}`;

                return (
                  <button
                    type="button"
                    key={integration.id}
                    className={`crmProviderBuCard${isSelected ? " crmProviderBuCard--selected" : ""}`}
                    onClick={() => selectIntegrationBusinessUnit(integration.business_unit_id)}
                  >
                    <div>
                      <div className="crmProviderBuName">{businessUnitName}</div>
                      <div className="crmProviderBuFoot">
                        Last event: {integration.last_event_type ? formatZadarmaEvent(integration.last_event_type) : "none yet"}
                      </div>
                    </div>
                    <div className="crmProviderBuMeta">
                      <span className={`crmProviderBadge crmProviderBadge--${integration.status || "draft"}`}>
                        {integration.status || "draft"}
                      </span>
                      <span
                        className={`crmProviderBadge crmProviderBadge--${
                          integration.credentials_status || "missing"
                        }`}
                      >
                        {integration.credentials_status || "missing"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="crmProviderBuEmpty">
              Prepare Zadarma for a Business Unit to see it here.
            </div>
          )}
        </section>

        <div className="crmProviderBody">
          <section className="crmProviderPanel">
            <h3 className="crmProviderPanelTitle">Configuration</h3>

            {error ? <div className="crmProviderState crmProviderState--error">{error}</div> : null}
            {message ? <div className="crmProviderState crmProviderState--info">{message}</div> : null}

            <div className="crmProviderGrid">
              <label className="crmProviderField crmProviderField--wide">
                <span>Business Unit</span>
                <select
                  value={selectedBusinessUnitId}
                  onChange={(event) => setSelectedBusinessUnitId(event.target.value)}
                >
                  {businessUnits.map((businessUnit) => (
                    <option key={businessUnit.id} value={businessUnit.id}>
                      {businessUnit.name}
                    </option>
                  ))}
                </select>
              </label>

              <div className="crmProviderState crmProviderState--info crmProviderField--wide">
                {selectedIntegration
                  ? "You are editing the Zadarma configuration for the selected Business Unit."
                  : "This Business Unit does not have a Zadarma configuration yet."}
              </div>

              <label className="crmProviderField">
                <span>Status</span>
                <select value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </label>

              <label className="crmProviderField">
                <span>Missed-call priority</span>
                <select
                  value={settings.missed_call_task_priority}
                  onChange={(event) => updateSetting("missed_call_task_priority", event.target.value)}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </label>

              <label className="crmProviderToggle crmProviderField--wide">
                <input
                  type="checkbox"
                  checked={Boolean(settings.create_missed_call_task)}
                  onChange={(event) => updateSetting("create_missed_call_task", event.target.checked)}
                />
                <span>Create follow-up task for missed or failed calls</span>
              </label>

              <label className="crmProviderToggle crmProviderField--wide">
                <input
                  type="checkbox"
                  checked={Boolean(settings.require_signature)}
                  onChange={(event) => updateSetting("require_signature", event.target.checked)}
                />
                <span>Require Zadarma Signature header when API secret is configured</span>
              </label>

              <div className="crmProviderWebRtcBox">
                <div className="crmProviderSubHeader">
                  <h4 className="crmProviderSubTitle">
                    <FiPhoneCall aria-hidden="true" />
                    <span>WebRTC widget</span>
                  </h4>
                  <span
                    className={`crmProviderBadge crmProviderBadge--${
                      settings.webrtc_widget_enabled ? "active" : "disabled"
                    }`}
                  >
                    {settings.webrtc_widget_enabled ? "enabled" : "disabled"}
                  </span>
                </div>

                <label className="crmProviderToggle crmProviderField--wide">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.webrtc_widget_enabled)}
                    onChange={(event) => updateSetting("webrtc_widget_enabled", event.target.checked)}
                  />
                  <span>Enable Zadarma WebRTC widget inside CRM</span>
                </label>

                <div className="crmProviderGrid">
                  <label className="crmProviderField">
                    <span>Language</span>
                    <select
                      value={settings.webrtc_widget_language || "es"}
                      onChange={(event) => updateSetting("webrtc_widget_language", event.target.value)}
                    >
                      <option value="es">Spanish</option>
                      <option value="en">English</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                      <option value="pl">Polish</option>
                      <option value="ua">Ukrainian</option>
                      <option value="ru">Russian</option>
                    </select>
                  </label>

                  <label className="crmProviderField">
                    <span>Shape</span>
                    <select
                      value={settings.webrtc_widget_shape || "square"}
                      onChange={(event) => updateSetting("webrtc_widget_shape", event.target.value)}
                    >
                      <option value="square">Square</option>
                      <option value="rounded">Rounded</option>
                    </select>
                  </label>

                  <label className="crmProviderField">
                    <span>Position</span>
                    <select
                      value={settings.webrtc_widget_position || "bottom_right"}
                      onChange={(event) => updateSetting("webrtc_widget_position", event.target.value)}
                    >
                      <option value="bottom_right">Bottom right</option>
                      <option value="bottom_left">Bottom left</option>
                      <option value="top_right">Top right</option>
                      <option value="top_left">Top left</option>
                    </select>
                  </label>

                  <label className="crmProviderField">
                    <span>Default SIP extension</span>
                    <input
                      value={settings.webrtc_widget_default_extension || ""}
                      onChange={(event) =>
                        updateSetting("webrtc_widget_default_extension", event.target.value)
                      }
                      placeholder="100"
                      autoComplete="off"
                    />
                  </label>
                </div>

                <p className="crmProviderHint">
                  The CRM domain must also be allowed in Zadarma Settings - Integrations and API - WebRTC widget
                  integration. The backend generates a temporary key per CRM user and SIP extension.
                </p>

                <div className="crmProviderActions">
                  <button
                    type="button"
                    className="crmProviderButton"
                    onClick={provisionWebRtcDomain}
                    disabled={webRtcProvisioning || !selectedIntegration?.has_credentials}
                  >
                    <FiGlobe aria-hidden="true" />
                    <span>{webRtcProvisioning ? "Provisioning WebRTC" : "Provision WebRTC domain"}</span>
                  </button>
                </div>

                <div className="crmProviderSubHeader" style={{ marginTop: "14px", marginBottom: "6px" }}>
                  <h4 className="crmProviderSubTitle">
                    <FiUsers aria-hidden="true" />
                    <span>User SIP extensions</span>
                  </h4>
                  <span className="crmProviderBadge crmProviderBadge--configured">
                    {selectedBusinessUnitUsers.length} users
                  </span>
                </div>

                {selectedBusinessUnitUsers.length ? (
                  <div className="crmProviderUserExtensionList">
                    {selectedBusinessUnitUsers.map((user) => (
                      <div key={user.id} className="crmProviderUserExtensionRow">
                        <div>
                          <div className="crmProviderUserName">{formatUserDisplayName(user)}</div>
                          <div className="crmProviderUserEmail">{user.email || `User ${user.id}`}</div>
                        </div>
                        <label className="crmProviderField">
                          <span>SIP</span>
                          <input
                            value={
                              (settings.webrtc_widget_user_extensions || {})[String(user.id)] || ""
                            }
                            onChange={(event) => updateUserExtension(user.id, event.target.value)}
                            placeholder={settings.webrtc_widget_default_extension || "100"}
                            autoComplete="off"
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="crmProviderEmpty">No active users for this Business Unit.</div>
                )}
              </div>

              <div className="crmProviderField">
                <span>Integration status</span>
                <span className={`crmProviderBadge crmProviderBadge--${selectedIntegration?.status || "draft"}`}>
                  {selectedIntegration?.status || "not prepared"}
                </span>
              </div>

              <div className="crmProviderField">
                <span>Credentials</span>
                <span
                  className={`crmProviderBadge crmProviderBadge--${
                    selectedIntegration?.credentials_status || "missing"
                  }`}
                >
                  {selectedIntegration?.credentials_status || "missing"}
                </span>
              </div>

              <label className="crmProviderField crmProviderField--wide">
                <span>Webhook URL</span>
                <div className="crmProviderWebhookBox">
                  <input value={selectedIntegration?.webhook_url || "Prepare integration first"} readOnly />
                  <button
                    type="button"
                    className="crmProviderButton"
                    onClick={copyWebhookUrl}
                    disabled={!selectedIntegration?.webhook_url}
                    title="Copy webhook URL"
                  >
                    <FiCopy aria-hidden="true" />
                  </button>
                </div>
              </label>

              <div className="crmProviderCredentialsBox">
                <div className="crmProviderCredentialsHeader">
                  <h4 className="crmProviderCredentialsTitle">
                    <FiShield aria-hidden="true" />
                    <span>Zadarma API credentials</span>
                  </h4>
                  <span
                    className={`crmProviderBadge crmProviderBadge--${
                      selectedIntegration?.credentials_status || "missing"
                    }`}
                  >
                    {selectedIntegration?.credentials_status || "missing"}
                  </span>
                </div>

                <div className="crmProviderGrid">
                  <label className="crmProviderField">
                    <span>API Key</span>
                    <input
                      value={credentialsForm.api_key}
                      onChange={(event) => updateCredentialsField("api_key", event.target.value)}
                      placeholder={selectedIntegration?.api_key_masked || "Paste Zadarma API key"}
                      autoComplete="off"
                      disabled={!selectedIntegration?.id || credentialsSaving}
                    />
                  </label>

                  <label className="crmProviderField">
                    <span>API Secret</span>
                    <input
                      type="password"
                      value={credentialsForm.api_secret}
                      onChange={(event) => updateCredentialsField("api_secret", event.target.value)}
                      placeholder={
                        selectedIntegration?.has_credentials
                          ? "Saved secret is hidden"
                          : "Paste Zadarma API secret"
                      }
                      autoComplete="new-password"
                      disabled={!selectedIntegration?.id || credentialsSaving}
                    />
                  </label>
                </div>

                <p className="crmProviderCredentialsHint">
                  Credentials are encrypted in the backend. After saving, the secret is cleared from this form and
                  webhook signature validation is enabled for Zadarma.
                </p>

                <div className="crmProviderActions">
                  <button
                    type="button"
                    className="crmProviderButton crmProviderButton--primary"
                    onClick={saveCredentials}
                    disabled={
                      credentialsSaving ||
                      !selectedIntegration?.id ||
                      !credentialsForm.api_key.trim() ||
                      !credentialsForm.api_secret.trim()
                    }
                  >
                    <FiSave aria-hidden="true" />
                    <span>{credentialsSaving ? "Saving" : "Save credentials"}</span>
                  </button>
                  <button
                    type="button"
                    className="crmProviderButton"
                    onClick={testConnection}
                    disabled={credentialsTesting || !selectedIntegration?.has_credentials}
                  >
                    <FiCheckCircle aria-hidden="true" />
                    <span>{credentialsTesting ? "Testing" : "Test connection"}</span>
                  </button>
                  <button
                    type="button"
                    className="crmProviderButton"
                    onClick={deleteCredentials}
                    disabled={credentialsDeleting || !selectedIntegration?.has_credentials}
                  >
                    <FiTrash2 aria-hidden="true" />
                    <span>{credentialsDeleting ? "Removing" : "Remove credentials"}</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="crmProviderActions">
              <button
                type="button"
                className="crmProviderButton crmProviderButton--primary"
                onClick={saveIntegration}
                disabled={saving || !selectedBusinessUnitId}
              >
                <FiSave aria-hidden="true" />
                <span>{selectedIntegration ? "Save settings" : "Prepare Zadarma"}</span>
              </button>
              <button
                type="button"
                className="crmProviderButton"
                onClick={rotateWebhookToken}
                disabled={saving || !selectedIntegration?.id}
              >
                <FiRotateCw aria-hidden="true" />
                <span>Rotate webhook token</span>
              </button>
            </div>
          </section>

          <section className="crmProviderPanel">
            <h3 className="crmProviderPanelTitle">Webhook events</h3>
            <div className="crmProviderState crmProviderState--info">
              Zadarma webhook deliveries appear here. Once credentials are saved, signature validation should show as
              valid for real events.
            </div>

            <div className="crmProviderTableShell">
              {logs.length ? (
                <table className="crmProviderTable">
                  <thead>
                    <tr>
                      <th>Received</th>
                      <th>Event</th>
                      <th>Call ID</th>
                      <th>Status</th>
                      <th>Signature</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td>{formatDateTime(log.created_at)}</td>
                        <td title={log.event_type || ""}>{formatZadarmaEvent(log.event_type)}</td>
                        <td>{log.external_conversation_id || "-"}</td>
                        <td>
                          <span className={`crmProviderBadge crmProviderBadge--${log.status || "received"}`}>
                            {log.status || "received"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`crmProviderBadge crmProviderBadge--${
                              log.signature_status || "not_configured"
                            }`}
                          >
                            {log.signature_status || "not_configured"}
                          </span>
                        </td>
                        <td>{log.error_message || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="crmProviderEmpty">
                  <FiZap aria-hidden="true" />
                  <span>{selectedIntegration ? "No webhook events yet" : "Prepare an integration first"}</span>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
